import { Toast } from '../../../components/Common/Toast';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { callAPI } from '../../../services/api.service';
import { BookModel } from '../../../interfaces/book.interface';
import { UserLocalStorageKeysEnum } from '../../../interfaces/user.interface';

// Mirrors the server-side cap in BookDataGeneration.controller.ts. Generation
// of more than this many books in one batch overwhelms the consumer's memory.
export const POST_FLYER_MAX_BOOKS = 50;

export const generatePdf = async (
  bookId: string,
  toggleLoaderPDF: Function,
  index: number,
  books: BookModel[],
  setBooks: Function,
): Promise<void> => {
  try {
    toggleLoaderPDF(index);
    const response = await callAPI(`api/pdf-generator/pdf?bookId=${bookId}`);

    if (response?.pdfUrl) {
      const updatedBooks = [...books];
      updatedBooks[index].pdfUrl = response.pdfUrl;
      setBooks(updatedBooks);
    }

    toggleLoaderPDF(index, false);
  } catch (error) {
    toggleLoaderPDF(index, false);
  }
};

export const showArchivedBooks = async (
  userEmail: string | undefined,
  archivedBooks: string,
  setLoading: Function,
  setBooks: Function,
  setTotalPages: Function,
): Promise<void> => {
  try {
    setLoading(true);
    const response = await callAPI(`api/booklist?email=${userEmail}&archived=${archivedBooks}`);

    if (response?.status === HTTPStatusEnum.OK) {
      setBooks(response?.books || []);
      setTotalPages(response?.totalPages);
    } else {
      Toast(response?.message || response?.error);
    }
  } catch (error) {
    Toast('Error fetching archived books', true);
  } finally {
    setLoading(false);
  }
};

export const fetchBooks = async (
  userEmail: string | undefined,
  archivedBooks: string,
  setLoading: Function,
  setBooks: Function,
  setTotalPages: Function,
): Promise<void> => {
  try {
    setLoading(true);
    const response = await callAPI(`api/booklist?email=${userEmail}&archived=${archivedBooks}`);

    if (response?.status === HTTPStatusEnum.OK) {
      setBooks(response?.books);
      setTotalPages(response?.totalPages);
    } else {
      Toast(response?.message || response?.error);
    }
  } catch (error) {
    Toast('Error fetching books', true);
  } finally {
    setLoading(false);
  }
};

export const updateBookText = async (
  bookId: string,
  pageIndex: number,
  updatedText: string,
  updatedScene: string,
): Promise<void> => {
  try {
    await callAPI('api/book-update/update-book-text', {
      bookId,
      pageIndex,
      updatedText,
      updatedScene,
    });
  } catch (error) {}
};

export const recreateImage = async (bookId: string, pageIndex?: number): Promise<void> => {
  try {
    const response = await callAPI('api/book-update/recreate-image', {
      bookId,
      pageIndex,
    });

    if (response?.status === HTTPStatusEnum.INTERNAL_SERVER_ERROR) {
      throw new Error('Image Not Generated');
    }
    return response;
  } catch (error) {
    throw new Error('Image Not Generated');
  }
};

export const printBooks = async (bookIds: Array<string>, setLoading: Function): Promise<void> => {
  if (bookIds.length > POST_FLYER_MAX_BOOKS) {
    Toast({
      title: 'Too many books selected',
      message: `You can generate flyers for up to ${POST_FLYER_MAX_BOOKS} books at a time. Please reduce your selection.`,
    });
    return;
  }
  try {
    setLoading(true);

    const requestData: any = { bookIds };
    const classroomId = localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_ID);
    if (classroomId) {
      requestData.classroomId = classroomId;
      requestData.projectId = localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID);
    }
    const response = await callAPI(`api/books/generate/flyers`, requestData, 'POST', {});

    if (response?.status === HTTPStatusEnum.CONFLICT) {
      Toast({
        title: 'Already generating',
        message: 'Flyer generation is already in progress. Check the status panel.',
      });
      return;
    }
    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Error generate selected books flyers', true);
      return;
    }
  } catch (error) {
    Toast('Error generate selected books flyers', true);
  } finally {
    setLoading(false);
  }
};

export const shipBooks = async (
  bookIds: Array<string>,
  setLoading: Function,
  onSuccess?: () => void,
): Promise<void> => {
  try {
    setLoading(true);
    const response = await callAPI(`api/book-purchase/bulk/ship`, { bookIds });

    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Books have been successfully sent to print');
      onSuccess && onSuccess();
    }
    if (response?.error) {
      Toast(response?.error, true);
    }
  } catch (error) {
    Toast('Error ship selected books', true);
  } finally {
    setLoading(false);
  }
};

// shipAllBooks / shipBooksConfirmation / fetchShipPreview live in
// Teacher/services/TeacherShipping.service.ts — the classroom shipping flow is a production
// teacher feature, not a debug helper.
